// iter 148 - Public "Find a Professional" page (/find-a-professional).
//
// Mirrors the new category-first accordion from the member portal
// (PortalDiscounts.js) but is PUBLIC - no auth gate, no portal shell,
// and built for SEO. Each accordion section carries a descriptive
// crawlable <h2> ("Mexico Immigration Attorneys", "Mexico Expat Tax
// Advisors", etc.) so Google can index each category as its own
// content cluster. Affiliate partners (SafetyWing/Airalo/Wise) are
// intentionally NOT rendered here - this surface is for the vetted
// human professionals only.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Scale, Banknote, HeartPulse, Umbrella, Cpu, MoreHorizontal,
  ChevronDown, ArrowRight, ShieldCheck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL || '';

// iter 148 - Category catalog. The H2/H2-blurb pair is now generated via
// `buildCategoryDefs(cityName)` below so the city-scoped landing pages
// (iter 149) can swap in city-specific copy without forking the file.
// The base (nationwide) defs are used when no city is bound.

// iter 149 - City slug map.
// `slug` is the URL segment (lowercase kebab).
// `displayName` must match the strings the admin uses in partner.cities[]
//   so the cityFilter substring lookup works.
// `seoCity` is the natural-English city name used in H1/H2/meta copy.
// `relocationPath` (iter 150) is the corresponding static relocation
//   guide URL when one exists - drives the reciprocal city-guide link
//   above the directory. `null` for cities without a static guide.
export const FAP_CITY_MAP = {
  'mexico-city':            { displayName: 'Mexico City',           seoCity: 'Mexico City',           relocationPath: '/relocation/cdmx' },
  'merida':                 { displayName: 'Mérida',                seoCity: 'Mérida',                relocationPath: '/relocation/merida' },
  'oaxaca':                 { displayName: 'Oaxaca',                seoCity: 'Oaxaca',                relocationPath: null },
  'guadalajara':            { displayName: 'Guadalajara',           seoCity: 'Guadalajara',           relocationPath: null },
  'puerto-vallarta':        { displayName: 'Puerto Vallarta',       seoCity: 'Puerto Vallarta',       relocationPath: '/relocation/puerto-vallarta' },
  'san-miguel-de-allende':  { displayName: 'San Miguel de Allende', seoCity: 'San Miguel de Allende', relocationPath: '/relocation/san-miguel-de-allende' },
  'monterrey':              { displayName: 'Monterrey',             seoCity: 'Monterrey',             relocationPath: null },
  'cancun':                 { displayName: 'Cancún',                seoCity: 'Cancún',                relocationPath: null },
  'cabo-san-lucas':         { displayName: 'Cabo San Lucas',        seoCity: 'Cabo San Lucas',        relocationPath: null },
};

// Build city-specific H2 + blurb. `cityName` is "" for the national page
// or the seoCity from FAP_CITY_MAP for a city-scoped page.
const buildCategoryDefs = (cityName) => {
  const c = (cityName || '').trim();
  const isCity = !!c;
  // Helper: keep H2 grammar natural for both surfaces.
  const inCity = isCity ? ` in ${c}` : ' in Mexico';
  return [
    {
      id: 'legal',
      label: 'Legal',
      icon: Scale,
      match: ['legal'],
      seoH2: isCity
        ? `${c} Immigration Attorneys for Americans`
        : 'Mexico Immigration Attorneys & Legal Services for Expats',
      seoBlurb: `Vetted bilingual immigration lawyers${inCity}. Permanent and temporary residency visas, RFC tax IDs, property purchase contracts, and family reunification.`,
    },
    {
      id: 'financial',
      label: 'Financial',
      icon: Banknote,
      match: ['financ', 'bank', 'tax', 'account'],
      seoH2: isCity
        ? `${c} Expat Tax Advisors & Cross-Border Accountants`
        : 'Mexico Expat Tax Advisors & Cross-Border Accountants',
      seoBlurb: `US-Mexico tax specialists${inCity} who handle FBAR, FATCA, and your annual return on both sides of the border. CPAs, CFPs, and licensed Mexican contadores trusted by American expats.`,
    },
    {
      id: 'healthcare',
      label: 'Healthcare',
      icon: HeartPulse,
      match: ['health', 'medic', 'dental', 'doctor', 'wellness'],
      seoH2: `English-Speaking Doctors & Healthcare Providers${inCity}`,
      seoBlurb: `Bilingual primary care, specialists, dentists, and wellness practitioners${inCity}. Pre-vetted for expat-friendly billing.`,
    },
    {
      id: 'insurance',
      label: 'Insurance',
      icon: Umbrella,
      match: ['insur'],
      seoH2: isCity
        ? `${c} Expat Health Insurance & Coverage Brokers`
        : 'Mexico Expat Health Insurance & Coverage Brokers',
      seoBlurb: `Licensed brokers${inCity} who quote Mexican private health insurance, IMSS supplemental coverage, and US travel-medical policies side-by-side. Independent - they shop the whole market for you.`,
    },
    {
      id: 'technology',
      label: 'Technology',
      icon: Cpu,
      match: ['tech', 'software', 'it ', 'internet', 'telecom'],
      seoH2: isCity
        ? `Internet, IT & Software Services in ${c}`
        : 'Internet, IT & Software Services for Expats in Mexico',
      seoBlurb: `Fiber install, business VPNs, phone-plan setup with US-number forwarding, and bilingual IT support for newcomers running US-based remote work${inCity}.`,
    },
    {
      id: 'other',
      label: 'Other',
      icon: MoreHorizontal,
      match: [],
      seoH2: isCity
        ? `Other Vetted Professional Services in ${c}`
        : 'Other Vetted Professional Services for American Expats in Mexico',
      seoBlurb: `Movers, real-estate buyers' agents, vehicle import specialists, schools, and other niche services${inCity} that don't fit a single bucket.`,
    },
  ];
};

const FindAProfessional = ({ citySlug }) => {
  // iter 149 - When `citySlug` is provided (city-scoped landing page),
  // we seed cityFilter to the city's displayName, lock it (no dropdown
  // override), and swap H1 + meta + category H2s to city-specific copy.
  const cityCfg = citySlug ? FAP_CITY_MAP[citySlug] : null;
  const lockedCity = cityCfg ? cityCfg.displayName : '';
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState(lockedCity);
  const [openCategory, setOpenCategory] = useState(null);

  // Whenever the route's citySlug changes (client-side nav between two
  // city pages), reset the active filter to the new locked city.
  useEffect(() => {
    setCityFilter(lockedCity);
  }, [lockedCity]);

  // iter 149 - Category definitions, recomputed when city changes.
  const CATEGORY_DEFS = useMemo(
    () => buildCategoryDefs(cityCfg ? cityCfg.seoCity : ''),
    [cityCfg],
  );

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/partners`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        setPartners(Array.isArray(d.partners) ? d.partners : []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Only the vetted human professionals on this surface.
  const vetted = useMemo(
    () => partners.filter((p) => p.partner_type === 'vetted_professional' && p.active !== false),
    [partners],
  );

  // iter 149 - Normalised string comparison. Partner city strings in
  // the DB are inconsistent (e.g. "Mexico City" vs "MEXICO CITY", and
  // "Merida" vs "Mérida"). Lower-case + strip diacritics so a city
  // filter matches every variant.
  const norm = (s) => (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const cityOptions = useMemo(() => {
    // De-duplicate by normalised key, keep the prettiest display variant
    // (one with mixed case + accents) so the dropdown reads cleanly.
    const byKey = new Map();
    vetted.forEach((p) => (p.cities || []).forEach((c) => {
      const k = norm(c);
      if (!k) return;
      const existing = byKey.get(k);
      // Prefer a value that has lowercase letters AND accents.
      const score = (s) => (/[a-z]/.test(s) ? 1 : 0) + (/[À-ÿ]/.test(s) ? 1 : 0);
      if (!existing || score(c) > score(existing)) byKey.set(k, c);
    }));
    return Array.from(byKey.values()).sort();
  }, [vetted]);

  const visibleVetted = useMemo(() => {
    if (!cityFilter) return vetted;
    const target = norm(cityFilter);
    return vetted.filter((p) => (p.cities || []).some((c) => norm(c) === target));
  }, [vetted, cityFilter]);

  const groupedByCategory = useMemo(() => {
    const groups = Object.fromEntries(CATEGORY_DEFS.map((c) => [c.id, []]));
    visibleVetted.forEach((p) => {
      const raw = (p.category || '').toLowerCase();
      const match = CATEGORY_DEFS.find((c) => c.id !== 'other' && c.match.some((m) => raw.includes(m)));
      groups[match ? match.id : 'other'].push(p);
    });
    return groups;
  }, [visibleVetted, CATEGORY_DEFS]);

  useEffect(() => {
    const firstWith = CATEGORY_DEFS.find((c) => (groupedByCategory[c.id] || []).length > 0);
    setOpenCategory((current) => {
      if (current && (groupedByCategory[current] || []).length > 0) return current;
      return firstWith ? firstWith.id : null;
    });
  }, [groupedByCategory, CATEGORY_DEFS]);

  // iter 149 - City-aware SEO. When citySlug is bound, build per-city
  // title + description so each landing page ranks for its own queries.
  const seoTitle = lockedCity
    ? `${lockedCity} Immigration Attorneys, Tax Advisors & Healthcare for American Expats | Mundalo`
    : 'Find a Mexico Professional - Vetted Lawyers, Accountants & Healthcare for American Expats | Mundalo';
  const seoDesc = lockedCity
    ? `Vetted bilingual immigration attorneys, US-Mexico tax advisors, English-speaking doctors and insurance brokers in ${lockedCity}, trusted by American expats. Browse the Mundalo directory free, no login required.`
    : 'Mundalo\'s vetted directory of immigration attorneys, US-Mexico tax advisors, English-speaking doctors, insurance brokers, and other professionals trusted by American expats in Mexico. Browse by category and city.';
  const seoKeywords = lockedCity
    ? `${lockedCity} immigration attorney for Americans, ${lockedCity} expat tax advisor, ${lockedCity} English-speaking doctor, ${lockedCity} expat insurance, ${lockedCity} relocation services`
    : 'Mexico immigration attorney, US-Mexico tax advisor, English-speaking doctors Mexico, expat insurance Mexico, Mexico relocation services, Mundalo partners';
  const canonical = lockedCity
    ? `https://mundalo.com/find-a-professional/${citySlug}`
    : 'https://mundalo.com/find-a-professional';
  const ogTitle = lockedCity
    ? `Find a Vetted Professional in ${lockedCity} - Mundalo`
    : 'Find a Mexico Professional - Mundalo';

  return (
    <div data-testid="find-a-professional-page" className="bg-[#F9F6EF] min-h-screen">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={seoKeywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: lockedCity ? `Mundalo Vetted Professionals - ${lockedCity}` : 'Mundalo Vetted Mexico Professionals',
          description: seoDesc,
          url: canonical,
          ...(lockedCity ? { about: { '@type': 'City', name: lockedCity } } : {}),
          publisher: { '@type': 'Organization', name: 'Mundalo', url: 'https://mundalo.com' },
        })}</script>
      </Helmet>

      <Header />

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-20">
        <header className="mb-10">
          <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E] mb-3" data-testid="fap-eyebrow">
            {lockedCity ? `Mundalo · ${lockedCity}` : 'Mundalo · Vetted Professionals'}
          </p>
          <h1
            className="font-display italic text-4xl md:text-6xl text-[#0A1F2E] leading-[1.05] mb-5"
            data-testid="fap-h1"
          >
            {lockedCity
              ? `Find a vetted ${lockedCity} professional you can trust.`
              : 'Find a Mexico professional you can actually trust.'}
          </h1>
          <p className="font-body text-base md:text-xl text-[#0A1F2E]/75 max-w-3xl leading-relaxed">
            {lockedCity
              ? `Immigration attorneys, US-Mexico tax accountants, bilingual doctors, insurance brokers, and other services in ${lockedCity} - all personally vetted by the Mundalo team. Free to browse. No login required.`
              : 'Immigration attorneys, US-Mexico tax accountants, bilingual doctors, insurance brokers, and other services personally vetted by the Mundalo team. Free to browse. No login required.'}
          </p>
          {lockedCity && (
            <p className="mt-4 text-sm">
              <Link
                to="/find-a-professional"
                data-testid="fap-back-to-national"
                className="text-[#1A5C6E] hover:text-[#2CA8A0] underline underline-offset-4"
              >
                ← See professionals across all of Mexico
              </Link>
            </p>
          )}
          {/* iter 150 - Reciprocal city-guide link. Surfaces above the
              directory whenever a static /relocation/{city} page exists,
              so visitors who land here from search can jump into the
              full Mundalo city guide. */}
          {lockedCity && cityCfg?.relocationPath && (
            <div
              className="mt-6 inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-[#EFE6CF]"
              data-testid="fap-city-guide-link-block"
            >
              <span className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">
                New to {lockedCity}?
              </span>
              <Link
                to={cityCfg.relocationPath}
                data-testid="fap-city-guide-link"
                className="text-sm font-semibold text-[#0A1F2E] hover:text-[#2CA8A0] inline-flex items-center gap-1.5 transition-colors"
              >
                Read the {lockedCity} relocation guide
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </header>

        {/* City filter - hidden on city-scoped pages (locked to that city). */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="fap-filter-row">
          {!lockedCity && cityOptions.length > 0 && (
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-[#EFE6CF] text-sm text-[#0A1F2E] rounded-full focus:outline-none focus:border-[#2CA8A0]"
              data-testid="fap-city-filter"
              aria-label="Filter by city"
            >
              <option value="">All cities</option>
              {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <p className="font-mono uppercase text-[10px] tracking-[0.16em] text-[#0A1F2E]/55">
            {visibleVetted.length} {visibleVetted.length === 1 ? 'professional' : 'professionals'}{lockedCity ? ` in ${lockedCity}` : (cityFilter ? ` in ${cityFilter}` : ' across Mexico')}
          </p>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-[#0A1F2E]/55">Loading…</div>
        ) : visibleVetted.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#0A1F2E]/65 bg-white border border-[#EFE6CF] rounded-xl">
            No professionals match the current filter.
          </div>
        ) : (
          <div
            className="bg-white border border-[#EFE6CF] rounded-2xl overflow-hidden"
            data-testid="fap-accordion"
          >
            {CATEGORY_DEFS.map((cat, idx) => {
              const list = groupedByCategory[cat.id] || [];
              const isOpen = openCategory === cat.id;
              const Icon = cat.icon;
              return (
                <section
                  key={cat.id}
                  className={idx === 0 ? '' : 'border-t border-[#EFE6CF]'}
                  data-testid={`fap-accordion-section-${cat.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenCategory(isOpen ? null : cat.id)}
                    disabled={list.length === 0}
                    data-testid={`fap-accordion-header-${cat.id}`}
                    aria-expanded={isOpen}
                    aria-controls={`fap-accordion-panel-${cat.id}`}
                    className={`w-full flex items-center gap-3 px-5 md:px-6 py-4 text-left transition ${
                      list.length === 0
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:bg-[#F9F6EF]'
                    }`}
                  >
                    <span className="w-8 h-8 bg-[#F4ECD8] rounded-lg flex items-center justify-center text-[#1A5C6E] flex-none">
                      <Icon className="w-4 h-4" />
                    </span>
                    <h2
                      className="font-display italic text-xl md:text-2xl text-[#0A1F2E] flex-1 leading-tight m-0"
                      data-testid={`fap-accordion-h2-${cat.id}`}
                    >
                      {cat.seoH2}
                    </h2>
                    <span
                      className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-[#F4ECD8] text-[#1A5C6E] font-mono text-[11px] tracking-[0.1em]"
                      data-testid={`fap-accordion-count-${cat.id}`}
                    >
                      {list.length}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-[#0A1F2E]/55 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    />
                  </button>

                  {isOpen && list.length > 0 && (
                    <div
                      id={`fap-accordion-panel-${cat.id}`}
                      className="border-t border-[#EFE6CF] bg-[#F9F6EF]/50"
                      data-testid={`fap-accordion-panel-${cat.id}`}
                    >
                      {/* SEO blurb - also reads as a friendly subhead */}
                      <p className="px-5 md:px-6 pt-4 pb-2 text-sm text-[#0A1F2E]/75 leading-relaxed">
                        {cat.seoBlurb}
                      </p>
                      <ul className="divide-y divide-[#EFE6CF]">
                        {list.map((p) => {
                          const cityLabel = (p.cities && p.cities[0]) || '';
                          return (
                            <li
                              key={p.id}
                              className="flex items-center gap-4 px-5 md:px-6 py-3.5"
                              data-testid={`fap-row-${p.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-[#0A1F2E] font-medium truncate">{p.name}</p>
                                <p className="font-mono uppercase text-[10px] tracking-[0.16em] text-[#1A5C6E] mt-0.5">
                                  {[cityLabel, p.category].filter(Boolean).join(' · ')}
                                </p>
                              </div>
                              <Link
                                to={`/start?return=${encodeURIComponent(`/portal/partners/${p.id}`)}`}
                                data-testid={`fap-row-view-${p.id}`}
                                className="flex-none inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0A1F2E] text-white text-xs font-mono uppercase tracking-[0.14em] hover:bg-[#163448] transition"
                              >
                                View Profile <ArrowRight className="w-3 h-3" />
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}

        {/* Trust + signup nudge */}
        <section className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-[#2CA8A0]" />
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E]">How we vet</p>
            </div>
            <p className="text-[#0A1F2E]/85 leading-relaxed text-sm">
              Every professional listed has been personally interviewed by the Mundalo team. We verify credentials, ask for two existing-client references, and review their bilingual capability before they appear on this directory.
            </p>
          </div>
          <div className="bg-[#0A1F2E] text-white rounded-2xl p-6">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mb-2">Going deeper</p>
            <p className="font-display italic text-xl md:text-2xl leading-tight mb-3">
              Want a Blueprint built around your exact move?
            </p>
            <Link
              to="/relocation/blueprint"
              data-testid="fap-blueprint-cta"
              className="inline-flex items-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-5 py-2.5 rounded-full text-sm transition-colors"
            >
              Get Your Blueprint <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FindAProfessional;
